import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ChevronRight } from 'lucide-react';
import AdminShell from '../../components/AdminShell.jsx';
import Alert from '../../components/Alert.jsx';
import Loader from '../../components/Loader.jsx';
import { api } from '../../api/client.js';
import { useAdminQueues } from '../../context/AdminQueueContext.jsx';

// /admin — the Command Deck. Landing page for the admin console: one status
// card per section, so "is anything waiting on me?" is answered without
// clicking through five tabs (docs/ADMIN_REFACTOR_BRIEF.md §6.1).
//
// Five independent fetches, each card degrading on its own. A card that fails
// shows a dash rather than taking the page down — this is a dashboard, not a
// transaction.

const ROSTER_TINT = ['--m', '--b', '', '', '', ''];

// Audit actions are namespaced ("user.update", "designRequest.transition") —
// tint the feed line by that namespace, matching the Audit Log page.
function actClass(action = '') {
  if (action.startsWith('user')) return ' adm-feed__act--user';
  if (action.startsWith('designRequest')) return ' adm-feed__act--design';
  return '';
}

function Portal({ className, title, onClick, children }) {
  return (
    <button type="button" className={`adm-portal adm-rv ${className}`} onClick={onClick}>
      <span className="adm-portal__head">
        {title}
        <ChevronRight size={12} aria-hidden="true" />
      </span>
      {children}
    </button>
  );
}

export default function CommandDeck() {
  const navigate = useNavigate();
  const { accessPending, designPending } = useAdminQueues();

  const [users, setUsers] = useState(null);
  const [accessRows, setAccessRows] = useState(null);
  const [grants, setGrants] = useState(null);
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.adminListUsers().catch(() => null),
      api.adminListAccessRequests('pending').catch(() => null),
      api.adminListGrants().catch(() => null),
      api.adminListAuditLog({ limit: 3 }).catch(() => null),
    ]).then(([u, a, g, l]) => {
      if (!alive) return;
      setUsers(u ? (u.users ?? []) : []);
      setAccessRows(a ? (a.requests ?? []) : []);
      setGrants(g ? (g.grants ?? []) : []);
      setAudit(l ? (l.entries ?? []) : []);
      if (!u && !a && !g && !l) setError('Could not reach the admin API.');
    });
    return () => { alive = false; };
  }, []);

  const loading = users === null;

  // Soft-deleted rows come back from /api/admin/users too — the roster count
  // means live accounts.
  const activeUsers = (users ?? []).filter((u) => !u.deletedAt);
  const grantTotal = (grants ?? []).length;
  const grantsProvisioned = (grants ?? []).filter((g) => g.htmlR2Key).length;
  const meterPct = grantTotal ? Math.round((grantsProvisioned / grantTotal) * 100) : 0;

  const hasAccess = !!accessPending;
  const hasDesign = !!designPending;

  return (
    <AdminShell crumb="Command Deck" userCount={activeUsers.length || undefined}>
      {error && <Alert kind="danger" onClose={() => setError(null)}>{error}</Alert>}

      <header className="adm-head adm-rv" style={{ animationDelay: '80ms' }}>
        <h1 className="adm-head__title">Command Deck</h1>
        <p className="adm-head__sub">Everything that needs a decision, at a glance.</p>
      </header>

      {loading ? (
        <Loader label="Reading the console…" />
      ) : (
        <div className="adm-deck">
          <Portal className="adm-deck__users" title="Users" onClick={() => navigate('/admin/users')}>
            <span className="adm-portal__body adm-portal__body--row" style={{ animationDelay: '180ms' }}>
              <span>
                <span className="adm-numeral adm-numeral--lg">{activeUsers.length}</span>
                <span className="adm-numeral-label">accounts</span>
              </span>
              <span>
                <span className="adm-numeral-label adm-numeral-label--flush">Roster</span>
                <span className="adm-roster" aria-hidden="true">
                  {activeUsers.slice(0, 6).map((u, i) => (
                    <span key={u.id} className={`adm-roster__dot adm-roster__dot${ROSTER_TINT[i] || ''}`}>
                      {u.username?.[0] ?? '?'}
                    </span>
                  ))}
                </span>
              </span>
              <span className="adm-portal__hint adm-portal__hint--blue adm-portal__hint--right">
                manage &rarr;
              </span>
            </span>
          </Portal>

          <Portal
            className="adm-deck__access"
            title="Access Requests"
            onClick={() => navigate('/admin/access-requests')}
          >
            <span className="adm-portal__body">
              {/* No giant numeral on a clear queue: it would be a lone Orbitron
                  zero, whose slashed glyph reads as a broken image — and a
                  shouted 0 is the opposite of calm. */}
              {hasAccess ? (
                <>
                  <span className="adm-numeral adm-numeral--xl adm-numeral--amber">
                    {accessPending}
                  </span>
                  <span className="adm-statusline adm-statusline--amber">
                    <span className="adm-dot adm-dot--amber" aria-hidden="true" />
                    Pending decisions
                  </span>
                  <ul className="adm-pending-list">
                    {(accessRows ?? []).slice(0, 3).map((r) => (
                      <li key={r.id}>
                        <span>{r.requester?.username ?? '(deleted)'}</span>
                        <span className="pkg">{r.package?.slug ?? r.package?.name ?? '—'}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <span className="adm-clear">
                  <CheckCircle2 size={30} aria-hidden="true" />
                  <span className="adm-clear__label">Queue clear</span>
                  <span className="adm-clear__sub">Nothing awaiting a decision.</span>
                </span>
              )}
              <span className="adm-portal__hint adm-portal__hint--amber adm-portal__hint--foot">
                review queue &rarr;
              </span>
            </span>
          </Portal>

          <Portal
            className="adm-deck__design"
            title="Design Requests"
            onClick={() => navigate('/admin/design-requests')}
          >
            <span className="adm-portal__body">
              {hasDesign ? (
                <>
                  <span className="adm-numeral adm-numeral--md adm-numeral--amber">
                    {designPending}
                  </span>
                  <span className="adm-statusline adm-statusline--amber">
                    <span className="adm-dot adm-dot--warning" aria-hidden="true" />
                    Awaiting triage
                  </span>
                </>
              ) : (
                <span className="adm-clear">
                  <CheckCircle2 size={26} aria-hidden="true" />
                  <span className="adm-clear__label">Nothing queued</span>
                </span>
              )}
            </span>
          </Portal>

          <Portal className="adm-deck__grants" title="Grants" onClick={() => navigate('/admin/grants')}>
            <span className="adm-portal__body">
              <span className="adm-numeral adm-numeral--md adm-numeral--blue">{grantsProvisioned}</span>
              <span
                className="adm-meter"
                role="img"
                aria-label={`${grantsProvisioned} of ${grantTotal} grants provisioned`}
              >
                <span className="adm-meter__fill" style={{ width: `${meterPct}%` }} />
              </span>
              <span className="adm-meter-label">
                <b>{grantsProvisioned}/{grantTotal}</b> provisioned
              </span>
            </span>
          </Portal>

          <Portal className="adm-deck__audit" title="Audit Log" onClick={() => navigate('/admin/audit-log')}>
            <span className="adm-portal__body adm-portal__body--feed">
              <ul className="adm-feed">
                {(audit ?? []).length === 0 ? (
                  <li>
                    <span className="adm-feed__act">—</span>
                    <span className="adm-feed__row">
                      <span className="adm-feed__who">No activity recorded yet</span>
                    </span>
                  </li>
                ) : (
                  (audit ?? []).map((e) => (
                    <li key={e.id}>
                      <span className={`adm-feed__act${actClass(e.action)}`}>{e.action}</span>
                      <span className="adm-feed__row">
                        <span className="adm-feed__who">
                          {e.actor?.username ?? 'system'}
                          <i aria-hidden="true">&rarr;</i>
                          {e.target?.username ?? '—'}
                        </span>
                        <span className="adm-feed__when">
                          {new Date(e.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </span>
                    </li>
                  ))
                )}
              </ul>
              <span className="adm-scanline" aria-hidden="true" />
            </span>
          </Portal>
        </div>
      )}
    </AdminShell>
  );
}
